// Колонки ряда входов: кадры, элементы, референсы, видео и звук. Раскладку
// колонок держит `PromptInputsRow`, здесь решается, какая колонка вообще есть
// при текущем режиме и чем она наполнена.

import { useTranslation } from "react-i18next";

import { ElementSlots } from "./ElementSlots";
import { FrameRoleSlots } from "./FrameRoleSlots";
import { MediaChannelSlots } from "./MediaChannelSlots";
import { PromptInputsRow } from "./PromptInputsRow";
import { ReferenceSlots } from "./ReferenceSlots";

import type { useChatAttachments } from "./useChatAttachments";
import type { UsePromptChannelsResult } from "./usePromptChannels";
import type { usePromptElements } from "./usePromptElements";

export interface PromptChannelsRowProps {
    disabled: boolean;
    attachments: ReturnType<typeof useChatAttachments>;
    elements: ReturnType<typeof usePromptElements>;
    channels: UsePromptChannelsResult;
    /** Элементы, чьё имя совпало со словом промпта — слот подсвечивает их. */
    conflictingElementIds: string[];
}

export function PromptChannelsRow({
    disabled,
    attachments,
    elements,
    channels,
    conflictingElementIds,
}: PromptChannelsRowProps) {
    const { t } = useTranslation("chat");

    return (
        // Кадры и элементы — один ряд колонок: у провайдера это разные каналы
        // одного запроса, и человеку они читаются вместе.
        <PromptInputsRow
            frames={
                attachments.hasImageRoles ? (
                    <FrameRoleSlots
                        roles={attachments.imageRoles}
                        itemByRole={attachments.itemByRole}
                        requiredCount={attachments.requiredRoleCount}
                        disabled={disabled}
                        accept={attachments.fileInputAccept}
                        onPickFiles={attachments.handlePickFilesForRole}
                        onPickFromUploaded={(role) =>
                            attachments.openPicker({ role }, attachments.setPickerOpen)
                        }
                        onPickFromGenerated={(role) =>
                            attachments.openPicker({ role }, attachments.setGeneratedPickerOpen)
                        }
                        onRemove={attachments.remove}
                        pairUnavailableHint={channels.framesHint}
                    />
                ) : undefined
            }
            elements={
                elements.supportsElements ? (
                    <ElementSlots
                        elements={elements.selectedElements}
                        maxElements={elements.limit}
                        disabled={disabled}
                        conflictingIds={conflictingElementIds}
                        unavailableHint={channels.elementsHint}
                        onOpenPicker={() => elements.setPickerOpen(true)}
                        onRemove={elements.toggle}
                        onView={elements.view}
                    />
                ) : undefined
            }
            references={
                // Канал объявлен моделью — колонку показываем и там, где его пока
                // некому исполнить: иначе Omni ничем не отличается от «Кадры →
                // Видео». Причина недоступности проговаривается в самом слоте.
                attachments.maxReferences > 0 ? (
                    <ReferenceSlots
                        maxReferences={attachments.maxReferences}
                        items={attachments.referenceItems}
                        disabled={disabled}
                        accept={attachments.fileInputAccept}
                        unavailableHint={channels.referencesHint}
                        onPickFiles={(files) => void attachments.uploadReference(files)}
                        onPickFromUploaded={() =>
                            attachments.openPicker({ channel: "reference" }, attachments.setPickerOpen)
                        }
                        onPickFromGenerated={() =>
                            attachments.openPicker(
                                { channel: "reference" },
                                attachments.setGeneratedPickerOpen,
                            )
                        }
                        onRemove={attachments.remove}
                    />
                ) : undefined
            }
            video={
                // Канал объявлен режимом — колонка есть. Модели, которые принимают
                // видео на вход (Seedance omni), раньше не показывали этого нигде:
                // файл уезжал безымянной плиткой из общей ленты.
                channels.maxInputVideos > 0 ? (
                    <MediaChannelSlots
                        max={channels.maxInputVideos}
                        items={channels.videoItems}
                        disabled={disabled}
                        accept="video/*"
                        addLabel={t("prompt.videoChannel.addSlot")}
                        removeLabel={t("prompt.videoChannel.remove")}
                        onPickFiles={attachments.handlePickFiles}
                        onPickFromUploaded={() =>
                            attachments.openPicker(undefined, attachments.setPickerOpen)
                        }
                        onPickFromGenerated={() =>
                            attachments.openPicker(undefined, attachments.setGeneratedPickerOpen)
                        }
                        onRemove={attachments.remove}
                    />
                ) : undefined
            }
            audio={
                // У звука нет ни библиотеки загруженного, ни генераций — источник
                // один, файл с диска.
                channels.maxInputAudio > 0 ? (
                    <MediaChannelSlots
                        max={channels.maxInputAudio}
                        items={channels.audioItems}
                        disabled={disabled}
                        accept="audio/*"
                        addLabel={t("prompt.audioChannel.addSlot")}
                        removeLabel={t("prompt.audioChannel.remove")}
                        onPickFiles={attachments.handlePickFiles}
                        onRemove={attachments.remove}
                    />
                ) : undefined
            }
        />
    );
}
